import type { PaginatedMeta } from "./altyn";

export type FriendlyPayStatus =
    | "created"
    | "initialized"
    | "success"
    | "failed"
    | "expired"
    | "cancelled";

export interface AdminFriendlyPayUserShort {
    id: number;
    name?: string;
    surname?: string;
    email?: string;
    telegram_username?: string;
    mobile?: string;
    country?: string;
}

export interface AdminFriendlyPayPaymentItem {
    id: number;
    user_id: number;
    user: AdminFriendlyPayUserShort | null;
    merchant_order_id: string;
    transaction_id: string | null;
    amount_rub: number;
    amount_usdt: number | null;
    rate: number | null;
    status: FriendlyPayStatus;
    qr_link: string | null;
    expire_at: string | null;
    credited_at: string | null;
    credited_amount_usdt: number | null;
    created_at: string | null;
    updated_at: string | null;
}

export interface AdminFriendlyPayPaymentDetail extends AdminFriendlyPayPaymentItem {
    processing_data: any;
    raw_webhook: any;
}

export interface AdminFriendlyPayPaymentsParams {
    per_page?: number;
    page?: number;
    sort_by?: "created_at" | "id" | "amount_rub" | "amount_usdt" | "status" | "user_id";
    sort_order?: "asc" | "desc";
    user_id?: number;
    status?: FriendlyPayStatus;
    transaction_id?: string;
    credited?: boolean;
    date_from?: string;
    date_to?: string;
}

export interface AdminFriendlyPayPaymentsResponse {
    success: boolean;
    data: AdminFriendlyPayPaymentItem[];
    meta: PaginatedMeta;
}

export interface AdminFriendlyPaySettingsResponse {
    success: boolean;
    data: {
        enabled: boolean;
    };
}

export interface UpdateAdminFriendlyPaySettingsRequest {
    enabled: boolean;
}

export interface AdminFriendlyPayStatsResponse {
    success: boolean;
    data: {
        total_payments: string;
        total_rub: string;
        total_transactions: number;
        success_transactions: number;
        pending_transactions: number;
        failed_transactions: number;
    };
}

export const friendlyPayEndpoints = {
    getAdminFriendlyPaySettings: {
        query: () => "/admin/friendlypay/settings",
        providesTags: ["FriendlyPaySettings"],
    },
    updateAdminFriendlyPaySettings: {
        query: (body: UpdateAdminFriendlyPaySettingsRequest) => ({
            url: "/admin/friendlypay/settings",
            method: "PUT",
            body,
        }),
        invalidatesTags: ["FriendlyPaySettings", "WalletTopUpMethods"],
    },
    getAdminFriendlyPayStats: {
        query: () => "/admin/friendlypay/stats",
        providesTags: ["FriendlyPayStats"],
    },
    getAdminFriendlyPayPayments: {
        query: (params?: AdminFriendlyPayPaymentsParams) => {
            const queryParams = new URLSearchParams();
            if (params?.per_page) queryParams.append("per_page", params.per_page.toString());
            if (params?.page) queryParams.append("page", params.page.toString());
            if (params?.sort_by) queryParams.append("sort_by", params.sort_by);
            if (params?.sort_order) queryParams.append("sort_order", params.sort_order);
            if (params?.user_id) queryParams.append("user_id", params.user_id.toString());
            if (params?.status) queryParams.append("status", params.status);
            if (params?.transaction_id) queryParams.append("transaction_id", params.transaction_id);
            if (params?.credited !== undefined) queryParams.append("credited", params.credited ? "1" : "0");
            if (params?.date_from) queryParams.append("date_from", params.date_from);
            if (params?.date_to) queryParams.append("date_to", params.date_to);

            const queryString = queryParams.toString();
            return `/admin/friendlypay/payments${queryString ? `?${queryString}` : ""}`;
        },
        providesTags: ["FriendlyPayPayments"],
    },
    getAdminFriendlyPayPayment: {
        query: (id: number) => `/admin/friendlypay/payments/${id}`,
        providesTags: ["FriendlyPayPayments"],
    },
};

export const friendlyPayTags = ["FriendlyPayPayments", "FriendlyPaySettings", "FriendlyPayStats"] as const;
